(function (root) {
  'use strict';
  const onePair = (variant, position) => /^1(?:\s|$)/.test(String(variant.options[position] || '').trim());
  const aggregate = variants => {
    const grouped = new Map();
    variants.forEach(variant => grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1));
    return [...grouped].map(([id, quantity]) => ({ id: Number(id), quantity }));
  };
  const packConfiguration = config => {
    const requested = Array.isArray(config.packCounts) ? config.packCounts.map(Number) : [];
    const counts = requested.join(',') === '1,4,8' ? [1, 4, 8] : [1, 2, 4];
    const requestedDefault = Number(config.defaultPackCount);
    const defaultCount = counts.includes(requestedDefault) ? requestedDefault : counts[1];
    return { counts, defaultCount, maximum: Math.max(...counts), previewOnly: config.previewOnly === true };
  };
  const singleVariants = (variants, pairPosition) => {
    const nativePosition = Number(pairPosition);
    const position = Number.isInteger(nativePosition) && nativePosition > 0 ? nativePosition - 1 : -1;
    return variants.filter(variant => position < 0 || onePair(variant, position));
  };
  const canFulfill = variants => {
    if (!Array.isArray(variants) || !variants.length || variants.some(variant => !variant || variant.available !== true)) return false;
    return aggregate(variants).every(item => {
      const variant = variants.find(candidate => Number(candidate.id) === item.id);
      const tracked = variant.inventoryManagement != null && variant.inventoryManagement !== '';
      if (!tracked || variant.inventoryPolicy === 'continue') return true;
      const quantity = Number(variant.inventoryQuantity);
      return Number.isFinite(quantity) && item.quantity <= Math.max(0, quantity);
    });
  };
  const priceFor = (variants, eligible = true) => {
    if (!Array.isArray(variants) || variants.some(variant => !variant || !Number.isSafeInteger(Number(variant.price)) || Number(variant.price) < 0)) throw Error('Invalid pack prices');
    const prices = variants.map(variant => Number(variant.price));
    const original = prices.reduce((total, price) => total + price, 0);
    if (!Number.isSafeInteger(original)) throw Error('Invalid pack prices');
    const free = eligible === true ? Math.floor(prices.length / 4) : 0;
    const saving = prices.slice().sort((a, b) => a - b).slice(0, free).reduce((total, price) => total + price, 0);
    return { original, saving, total: original - saving };
  };
  const optionText = value => String(value == null ? '' : value).trim().replace(/\s+/g, ' ');
  const groupChoices = (model, chosen) => {
    const choices = [];
    model.groups.forEach((records, label) => {
      const matches = records.filter(record => model.independent.every(position => record.options[position] === chosen.options[position]));
      if (!matches.length) return;
      const record = matches.find(item => item.id === chosen.id) || matches.find(item => item.variant.available) || matches[0];
      choices.push({ value: record.id, label, disabled: !model.previewOnly && !record.variant.available });
    });
    return choices;
  };
  const nextForOption = (model, chosen, position, value) => {
    const pool = model.records.filter(record => (record.variant.available || model.previewOnly) && record.options[position] === optionText(value)
      && model.independent.every(index => index === position || record.options[index] === chosen.options[index]));
    return pool.find(record => record.label === chosen.label)
      || pool.find(record => record.options[model.color] === chosen.options[model.color])
      || (model.pair >= 0 ? pool.find(record => record.pack === chosen.pack) : null)
      || pool[0];
  };
  root.FrilloraPackModel = Object.freeze({ onePair, aggregate, priceFor, packConfiguration, singleVariants, canFulfill, groupChoices, nextForOption });
  if (typeof document === 'undefined') return;
  const instances = new Map();
  function mount(panel) {
    if (panel.dataset.frPackReady || typeof root.FrilloraCartEnhance?.addItems !== 'function'
      || typeof root.FrilloraColorPackCore?.createModel !== 'function'
      || typeof root.FrilloraPdpColorSwatches?.create !== 'function') return;
    const form = panel.closest('form.fr-product-form');
    const scope = form?.closest('.fr-pdp');
    const card = form?.closest('.fr-pdp-card');
    if (!form || !scope || !card || document.documentElement.classList.contains('fr-cart-pdp-embed')) return;
    let config;
    try { config = JSON.parse(panel.querySelector('[data-fr-mix-pack-data]').textContent); } catch { return; }
    if (config.enabled !== true) return;
    const options = packConfiguration(config);
    const scopedPack = options.previewOnly || options.counts.join(',') === '1,4,8';
    const singles = scopedPack ? singleVariants(config.variants, config.pairPosition) : config.variants;
    let model;
    try { model = root.FrilloraColorPackCore.createModel(singles, config); } catch { return; }
    if (!model || !model.records.length) return;
    model.previewOnly = options.previewOnly;
    const colorIndex = model.color;
    const independent = model.independent;
    const eligible = config.buy3Get1Eligible === true;
    const requiresSizeIntent = ['10310901563584','10310901760192','10310901825728'].includes(String(config.productId));
    const entryUrl = new URL(document.baseURI);
    const requestedColor = requiresSizeIntent ? entryUrl.searchParams.get('color') : null;
    const explicitVariant = requiresSizeIntent && !requestedColor && singles.find(variant => Number(variant.id) === Number(entryUrl.searchParams.get('variant')));
    const colorVariant = requestedColor && singles.find(variant => variant.options[colorIndex] === requestedColor && (variant.available || options.previewOnly));
    const currentId = Number(colorVariant?.id || form.querySelector('[data-product-variant-id]')?.value || config.currentVariantId);
    const initial = scopedPack
      ? singles.find(variant => Number(variant.id) === currentId && (variant.available || options.previewOnly)) || singles.find(variant => variant.available || options.previewOnly)
      : singles.find(variant => Number(variant.id) === currentId);
    if (!initial || !options.previewOnly && !initial.available) return;
    if (!card.querySelector('[data-fr-color-pack-widget] [data-fr-pdp-color-swatches]')) return;
    const nativeOptionTemplates = new Map(independent.map(position => [position, new Map()]));
    const nativeOptionGroups = new Map();
    card.querySelectorAll('[data-product-option-button]').forEach(button => {
      const position = Number(button.dataset.optionPosition) - 1;
      if (!nativeOptionTemplates.has(position)) return;
      nativeOptionTemplates.get(position).set(optionText(button.dataset.optionValue), button.cloneNode(true));
      if (!nativeOptionGroups.has(position)) nativeOptionGroups.set(position, button.parentElement.cloneNode(false));
    });
    for (const position of independent) {
      if (!nativeOptionGroups.has(position) || model.records.some(record => !nativeOptionTemplates.get(position).has(record.options[position]))) return;
    }
    const decode = value => String(value).replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    const labels = Object.fromEntries(Object.entries(config.labels).map(([key, value]) => [key, decode(value)]));
    const initialCount = !scopedPack ? 2 : options.previewOnly || canFulfill(Array.from({ length: options.defaultCount }, () => initial))
      ? options.defaultCount : options.counts.find(count => canFulfill(Array.from({ length: count }, () => initial))) || options.counts[0];
    const state = { count: initialCount, mode: 'same', selected: Array.from({ length: options.maximum }, () => Number(initial.id)), confirmed: Array(options.maximum).fill(!requiresSizeIntent || Boolean(explicitVariant && Number(explicitVariant.id) === Number(initial.id))), busy: false };
    const sizeConfirmed = () => !requiresSizeIntent || state.confirmed.slice(0, state.count).every(Boolean);
    const byId = id => singles.find(variant => Number(variant.id) === Number(id));
    const selectedFor = count => state.selected.slice(0, count).map(byId);
    const money = cents => {
      try { return new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(cents / 100); }
      catch { return (cents / 100).toFixed(2) + ' ' + config.currency; }
    };
    const shorten = value => String(value || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
    const node = (tag, className, text) => {
      const result = document.createElement(tag);
      if (className) result.className = className;
      if (text !== undefined) result.textContent = text;
      return result;
    };
    const nativeClone = template => {
      const clone = template.cloneNode(true);
      if (options.previewOnly) {
        clone.querySelectorAll('[data-option-sold-out]').forEach(element => element.remove());
      }
      [clone, ...clone.querySelectorAll('*')].forEach(element => {
        if (options.previewOnly) {
          element.style.removeProperty('display');
          element.style.removeProperty('visibility');
          ['hidden', 'aria-hidden', 'disabled', 'aria-disabled', 'data-fr-oos'].forEach(attribute => element.removeAttribute(attribute));
          element.classList.remove('is-disabled', 'is-unavailable', 'is-sold-out');
        }
        [...element.attributes].forEach(attribute => {
          if (attribute.name === 'id' || /^data-(?:product-|option-|selected-option-|pdp-|fr-pdp-swatch-value)/.test(attribute.name)) element.removeAttribute(attribute.name);
        });
      });
      return clone;
    };
    const primary = node('div', 'fr-mix-pack__primary');
    panel.prepend(primary);
    const blocked = () => state.busy || !options.previewOnly && Boolean(root.FrilloraCartEnhance?.isVariantSelectionBlocked?.());
    let syncingNative = false;
    let colorPickers = [];
    function syncNative(force = false) {
      const variant = byId(state.selected[0]);
      const input = form.querySelector('[data-product-variant-id]');
      if (!force && Number(input?.value) === Number(variant.id)) return true;
      const detail = { variantId: String(variant.id), allowUnavailable: options.previewOnly, updateUrl: true, reason: 'mix-pack', applied: false };
      syncingNative = true;
      try { card.dispatchEvent(new CustomEvent('fr:select-variant', { detail })); }
      finally { syncingNative = false; }
      return detail.applied && Number(input?.value) === Number(variant.id);
    }
    function choose(index, variant, focusKey) {
      if (blocked() || !variant || !options.previewOnly && !variant.available) return false;
      const previous = state.selected.slice();
      const previousConfirmed = state.confirmed.slice();
      if (requiresSizeIntent && focusKey?.style) {
        state.confirmed[index] = true;
        if (state.mode === 'same') state.confirmed.fill(true);
      }
      state.selected[index] = Number(variant.id);
      if (state.mode === 'same') state.selected.fill(Number(variant.id));
      if (!syncNative()) {
        state.selected = previous;
        state.confirmed = previousConfirmed;
        panel.querySelector('[data-fr-pack-status]').textContent = labels.unavailable;
        return false;
      }
      panel.querySelector('[data-fr-pack-status]').textContent = '';
      render(focusKey);
      return true;
    }
    function picker(index, isPrimary) {
      const variant = byId(state.selected[index]);
      const chosen = model.byId(variant.id);
      const row = node('div', isPrimary ? 'fr-mix-pack__row fr-mix-pack__row--primary' : 'fr-mix-pack__row');
      row.dataset.frPackRow = String(index);
      if (!isPrimary) {
        const header = node('div', 'fr-mix-pack__row-head');
        header.append(node('strong', 'fr-mix-pack__row-label', labels.pair + ' ' + (index + 1)), node('span', 'fr-mix-pack__row-choice', chosen.label));
        row.append(header);
      }
      const colorPicker = root.FrilloraPdpColorSwatches.create({
        scope: card, model, entries: groupChoices(model, chosen), value: chosen.id,
        label: labels.color, soldOutLabel: labels.soldOut, disabled: blocked(),
        onChange(value) {
          const candidate = model.byId(value);
          return choose(index, candidate?.variant, { group: candidate?.label, index: String(index) });
        }
      });
      colorPickers.push(colorPicker);
      colorPicker.querySelectorAll('[data-fr-pdp-swatch-value]').forEach(button => {
        const record = model.byId(button.dataset.frPdpSwatchValue);
        button.dataset.frPackColor = record.options[colorIndex];
        button.dataset.frPackGroup = record.label;
        button.dataset.frPackIndex = String(index);
      });
      row.append(colorPicker);
      independent.forEach(position => {
        row.append(node('div', 'fr-product-form__label', config.optionNames[position] + ': ' + (requiresSizeIntent && !state.confirmed[index] ? labels.chooseStyle : shorten(chosen.options[position]))));
        const styleGroup = nativeClone(nativeOptionGroups.get(position));
        styleGroup.setAttribute('aria-label', config.optionNames[position] || labels.style);
        const styles = [...new Set(model.records.map(record => record.options[position]))];
        styles.forEach(style => {
          const candidate = nextForOption(model, chosen, position, style);
          const button = nativeClone(nativeOptionTemplates.get(position).get(style));
          button.type = 'button';
          button.title = style;
          if ([10310901563584, 10310901760192, 10310901825728].includes(Number(config.productId))) {
            const ageLabel = style.match(/^(XXS|XS|S|M|L|XL)\s+\(([^)]+)\)$/);
            const displayLabel = button.querySelector(':scope > span');
            if (displayLabel && (ageLabel || style === 'Adult One Size')) {
              button.dataset.frSizeLines = 'true';
              button.setAttribute('aria-label', style);
              displayLabel.replaceChildren(
                node('span', 'fr-size-line-code', ageLabel ? ageLabel[1] : 'Adult'),
                node('span', 'fr-size-line-age', ageLabel ? '(' + ageLabel[2] + ')' : 'One Size')
              );
            }
          }
          const selectedStyle = (!requiresSizeIntent || state.confirmed[index]) && style === chosen.options[position];
          button.setAttribute('aria-pressed', String(selectedStyle));
          button.classList.toggle('is-active', selectedStyle);
          button.classList.toggle('is-disabled', !candidate);
          button.dataset.frPackStyle = style;
          button.dataset.frPackPosition = String(position);
          button.dataset.frPackIndex = String(index);
          button.disabled = blocked() || !candidate;
          button.addEventListener('click', event => {
            event.preventDefault(); event.stopImmediatePropagation();
            choose(index, candidate?.variant, { style, position: String(position), index: String(index) });
          });
          styleGroup.append(button);
        });
        row.append(styleGroup);
      });
      return row;
    }
    function updatePurchaseButtons() {
      if (!scopedPack) return;
      const pendingSize = !sizeConfirmed();
      const cartPurchaseBlocked = Boolean(root.FrilloraCartEnhance?.isPurchaseBlocked?.());
      const purchaseUnavailable = options.previewOnly || !canFulfill(selectedFor(state.count));
      scope.dataset.frSizeConfirmed = String(!pendingSize);
      scope.querySelectorAll('form.fr-product-form button[type="submit"], .fr-mobile-atc__form button[type="submit"]').forEach(button => {
        const disabled = blocked() || cartPurchaseBlocked || pendingSize || purchaseUnavailable || button.name === 'return_to' && state.count > 1;
        if (button.disabled !== disabled) button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
        const label = options.previewOnly ? labels.previewUnavailable : pendingSize ? labels.chooseStyle : purchaseUnavailable ? labels.unavailable : button.dataset.addLabel;
        if (label && button.textContent !== label) button.textContent = label;
      });
    }
    function render(requestedFocus) {
      const focused = document.activeElement;
      const focusKey = requestedFocus || (focused?.closest('[data-fr-mix-pack]') === panel
        ? { group: focused.dataset.frPackGroup, style: focused.dataset.frPackStyle, position: focused.dataset.frPackPosition, index: focused.dataset.frPackIndex } : null);
      colorPickers.forEach(picker => picker.frColorDropdown.destroy());
      colorPickers = [];
      primary.replaceChildren(picker(0, true));
      panel.querySelectorAll('[data-fr-pack]').forEach(button => {
        const count = Number(button.dataset.frPack);
        const price = priceFor(selectedFor(count), eligible);
        button.setAttribute('aria-pressed', String(count === state.count));
        button.disabled = blocked() || scopedPack && (!options.counts.includes(count) || !options.previewOnly && !canFulfill(selectedFor(count)));
        button.querySelector('[data-fr-pack-price]').textContent = money(price.total);
        const compare = button.querySelector('[data-fr-pack-compare]');
        compare.hidden = !price.saving;
        compare.textContent = money(price.original);
        const unit = button.querySelector('[data-fr-pack-unit]');
        unit.hidden = count === 1;
        button.querySelector('[data-fr-pack-unit-price]').textContent = money(price.total / count);
        const cardSaving = button.querySelector('[data-fr-pack-card-save]');
        cardSaving.hidden = !price.saving;
        cardSaving.textContent = labels.save + ' ' + Math.round(price.saving / price.original * 100) + '%';
        const offer = button.querySelector('[data-fr-pack-offer]');
        if (offer) offer.hidden = !eligible;
        const shippingRule = config.shippingRule || config.shippingRules?.[config.shippingCountry];
        const configuredShipping = shippingRule?.currency === config.currency ? shippingRule : null;
        const shipping = configuredShipping || root.FrilloraMarketShipping?.snapshot?.(config.currency)
          || (config.currency === 'USD' && config.shippingCountry === 'US' ? { threshold: 6500, standardFee: 800 } : null);
        const shippingLabel = button.querySelector('[data-fr-pack-shipping]');
        shippingLabel.classList.toggle('is-free', Boolean(shipping && price.total >= shipping.threshold));
        shippingLabel.textContent = shipping
          ? (price.total >= shipping.threshold ? labels.shippingFree : labels.shippingFee)
              .replace('{threshold}', money(shipping.threshold)).replace('{fee}', money(shipping.standardFee))
          : labels.shippingCheckout;
      });
      panel.querySelectorAll('[data-fr-pack-mode]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.frPackMode === state.mode));
        button.disabled = blocked();
      });
      panel.querySelector('[data-fr-pack-customize]').hidden = state.count === 1;
      const rows = panel.querySelector('[data-fr-pack-rows]');
      rows.hidden = state.mode !== 'mix';
      rows.replaceChildren(...(state.mode === 'mix' ? Array.from({ length: state.count }, (_, index) => picker(index, false)) : []));
      const price = priceFor(selectedFor(state.count), eligible);
      panel.querySelector('[data-fr-pack-subtotal]').textContent = money(price.total);
      const compare = panel.querySelector('[data-fr-pack-subtotal-compare]');
      compare.hidden = !price.saving;
      compare.textContent = money(price.original);
      const saving = panel.querySelector('[data-fr-pack-saving]');
      saving.hidden = !price.saving;
      saving.textContent = labels.save + ' ' + money(price.saving);
      panel.querySelector('[data-fr-pack-help]').textContent = (price.saving ? labels.discountNote + ' ' : '') + labels.pricingNote;
      updatePurchaseButtons();
      if (requiresSizeIntent) {
        const pending = !sizeConfirmed();
        panel.querySelector('[data-fr-pack-status]').textContent = pending ? labels.chooseStyle : '';
        if (!state.confirmed[0]) scope.querySelectorAll('[data-hosiery-size-row]').forEach(row => { row.classList.remove('is-selected'); row.setAttribute('aria-current','false'); });
        const url = new URL(document.baseURI);
        if (state.confirmed[0]) { url.searchParams.delete('color'); url.searchParams.set('variant',String(state.selected[0])); }
        else { url.searchParams.delete('variant'); url.searchParams.set('color',byId(state.selected[0]).options[colorIndex]); }
        if (url.href !== location.href) history.replaceState(history.state,'',url.href);
      }
      if (options.previewOnly) panel.querySelector('[data-fr-pack-status]').textContent = labels.previewUnavailable;
      scope.querySelectorAll('[data-sticky-price]').forEach(element => element.textContent = money(price.total));
      scope.querySelectorAll('[data-sticky-meta]').forEach(element => element.textContent = state.count + ' ' + (state.count === 1 ? labels.pair : labels.pairs));
      scope.querySelectorAll('[data-fr-sticky-quantity-control], [data-pdp-quantity]').forEach(element => {
        element.hidden = true;
        element.style.setProperty('display', 'none', 'important');
      });
      // FRILLORA-PACK-PROMO-SYNC-20261101-v1
      // The controller is registered before its first render, so default selection
      // and subsequent color, style, count and mode changes use the same read API.
      document.dispatchEvent(new CustomEvent('frillora:pack-selection-change', { detail: { form, scope } }));
      if (focusKey?.group || focusKey?.style) {
        [...panel.querySelectorAll('button')].find(button => button.dataset.frPackIndex === focusKey.index
          && (focusKey.group ? button.dataset.frPackGroup === focusKey.group
            : button.dataset.frPackStyle === focusKey.style && button.dataset.frPackPosition === focusKey.position))?.focus({ preventScroll: true });
      }
    }
    panel.querySelectorAll('[data-fr-pack]').forEach(button => button.addEventListener('click', () => {
      if (blocked()) return;
      const count = Number(button.dataset.frPack);
      if (scopedPack && !options.counts.includes(count)) return;
      state.count = count;
      render();
    }));
    panel.querySelectorAll('[data-fr-pack-mode]').forEach(button => button.addEventListener('click', () => {
      if (blocked()) return;
      state.mode = button.dataset.frPackMode;
      if (state.mode === 'same') { state.selected.fill(state.selected[0]); state.confirmed.fill(state.confirmed[0]); }
      render();
    }));
    const controller = {
      form, scope,
      items() {
        const selected = selectedFor(state.count);
        if (!sizeConfirmed()) throw Error(labels.chooseStyle);
        if (options.previewOnly) throw Error(labels.previewUnavailable);
        if (scopedPack) {
          if (!options.counts.includes(state.count) || selected.length !== state.count || !canFulfill(selected)) throw Error(labels.unavailable);
        } else if (selected.length !== state.count || selected.some(variant => !variant?.available)) throw Error(labels.unavailable);
        return aggregate(selected);
      },
      state() { return { count: state.count, mode: state.mode, selected: state.selected.slice(0, state.count), sizeConfirmed: sizeConfirmed(), confirmed: state.confirmed.slice(0,state.count), price: priceFor(selectedFor(state.count), eligible) }; },
      render
    };
    instances.set(panel, controller);
    panel.dataset.frPackReady = 'true';
    form.dataset.frPackActive = 'true';
    scope.dataset.frPackActive = 'true';
    panel.hidden = false;
    if (scopedPack) scope.addEventListener('submit', event => {
      if (!event.target.matches('.fr-product-form,.fr-mobile-atc__form')) return;
      if (!sizeConfirmed() || options.previewOnly || !canFulfill(selectedFor(state.count)) || event.submitter?.name === 'return_to' && state.count > 1) {
        event.preventDefault();
        event.stopImmediatePropagation();
        panel.querySelector('[data-fr-pack-status]').textContent = !sizeConfirmed() ? labels.chooseStyle : options.previewOnly ? labels.previewUnavailable : labels.unavailable;
      }
    }, true);
    document.addEventListener('frillora:pack-cart-state' , event => { state.busy = Boolean(event.detail.busy); render(); });
    document.addEventListener('frillora:pack-added', () => { panel.querySelector('[data-fr-pack-status]').textContent = labels.added; });
    card.addEventListener('fr:variant-selected', () => { if (!state.busy && !syncingNative) render(); });
    if (requiresSizeIntent) {
      const observer = new MutationObserver(updatePurchaseButtons);
      scope.querySelectorAll('form.fr-product-form button[type="submit"], .fr-mobile-atc__form button[type="submit"]').forEach(button => observer.observe(button,{attributes:true,attributeFilter:['disabled'],childList:true,subtree:true}));
      syncNative(Boolean(requestedColor && colorVariant));
      if (requestedColor && colorVariant) {
        const applyEntryColor = () => {
          scope.dataset.frInitialProductCover = 'false';
          syncNative(true);
          render();
        };
        root.requestAnimationFrame(applyEntryColor);
        root.addEventListener('load',applyEntryColor,{once:true});
      }
    }
    render();
  }
  root.FrilloraPackSelection = Object.freeze({
    itemsForForm(form) {
      for (const instance of instances.values()) {
        if (instance.form === form || instance.scope.contains(form) && form.matches('.fr-mobile-atc__form')) return instance.items();
      }
      return null;
    },
    getState() { return [...instances.values()].map(instance => instance.state()); }
  });
  let attempts = 0;
  const initialize = () => {
    document.querySelectorAll('[data-fr-mix-pack]').forEach(mount);
    if (!instances.size && document.querySelector('[data-fr-mix-pack]') && attempts++ < 200) root.setTimeout(initialize, 50);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize); else initialize();
  document.addEventListener('shopify:section:load', initialize);
})(globalThis);

